import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import ProductCard from "./ProductCard";
import { getProducts } from "../services/product.service";
import { getCategories } from "../services/category.service";

const ShopSection = () => {
  const [grid, setGrid] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("default");
  const [priceRange, setPriceRange] = useState([0, 10000]);

  const location = useLocation();

  const getProductPrice = (product) => {
    if (product.variants && product.variants.length > 0) {
      return Math.min(...product.variants.map(v => v.price));
    }
    return product.price || 0;
  };

  const sidebarController = () => {
    setActive(!active);
  };

  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // Category Filter
    if (selectedCategory) {
      filtered = filtered.filter(p =>
        p.categoryId === selectedCategory.id ||
        p.categoryName === selectedCategory.name
      );
    }

    // Search Filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.name?.toLowerCase().includes(search) ||
        p.categoryName?.toLowerCase().includes(search) ||
        p.description?.toLowerCase().includes(search)
      );
    }

    // Price Filter
    filtered = filtered.filter(product => {
      const price = getProductPrice(product);
      return price >= priceRange[0] && price <= priceRange[1];
    });

    // Sorting
    if (sortBy === "price-low") {
      filtered.sort((a, b) => getProductPrice(a) - getProductPrice(b));
    } else if (sortBy === "price-high") {
      filtered.sort((a, b) => getProductPrice(b) - getProductPrice(a));
    } else if (sortBy === "name-az") {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === "newest") {
      filtered.sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds);
    }

    return filtered;
  }, [products, selectedCategory, searchTerm, priceRange, sortBy]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodData, catData] = await Promise.all([
          getProducts(),
          getCategories()
        ]);

        setProducts(prodData);
        setCategories(catData);

        // Handle category and search from URL
        const params = new URLSearchParams(location.search);
        const categoryName = params.get("category");
        const searchParam = params.get("search");

        if (categoryName && categoryName !== "all") {
          const cat = catData.find(c => c.name === categoryName);
          if (cat) {
            setSelectedCategory(cat);
          } else {
            setSelectedCategory(null);
          }
        } else {
          setSelectedCategory(null);
        }

        if (searchParam) {
          setSearchTerm(searchParam);
        } else {
          setSearchTerm("");
        }
      } catch (error) {
        console.error("Error fetching shop data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [location.search]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    if (window.innerWidth < 992) setActive(false);
  };

  // const handlePriceChange = (newRange) => {
  //   setPriceRange(newRange);
  // };

  // const handleFilterClick = () => {
  //   applyFilters(products, priceRange, selectedCategory);
  // };

  if (loading) return <div className="py-80 text-center"><div className="spinner-border text-main-600"></div></div>;

  return (
    <section className='shop py-80' style={{ overflow: 'visible' }}>
      <style>{`
        @media (min-width: 992px) {
            .shop-sidebar {
                position: sticky !important;
                top: 160px !important;
                align-self: flex-start !important;
                background: white !important;
                padding: 15px !important;
                border-radius: 20px !important;
                box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05) !important;
                z-index: 9 !important;
            }
            /* Hide the default scrollbar for a cleaner look */
            .shop-sidebar::-webkit-scrollbar {
                width: 4px;
            }
            .shop-sidebar::-webkit-scrollbar-thumb {
                background: #e5e7eb;
                border-radius: 10px;
            }
        }
      `}</style>
      <div className={`side-overlay ${active && "show"}`}></div>
      <div className='container container-lg'>
        <div className='row' style={{ overflow: 'visible', alignItems: 'flex-start' }}>
          {/* Sidebar Start */}
          <div className='col-lg-3' style={{ overflow: 'visible' }}>
            <div className={`shop-sidebar ${active && "active"}`}>
              <button
                onClick={sidebarController}
                type='button'
                className='shop-sidebar__close d-lg-none d-flex w-32 h-32 flex-center border border-gray-100 rounded-circle hover-bg-main-600 position-absolute inset-inline-end-0 me-10 mt-8 hover-text-white hover-border-main-600'
              >
                <i className='ph ph-x' />
              </button>

              <div className='shop-sidebar__box border border-gray-100 rounded-16 p-32 mb-32 bg-white shadow-sm'>
                <h6 className='text-xl border-bottom border-gray-100 pb-24 mb-24 fw-bold'>
                  Categories
                </h6>
                <ul className='scroll-sm'>
                  <li className='mb-12'>
                    <button
                      onClick={() => handleCategoryClick(null)}
                      className={`text-gray-900 hover-text-main-600 bg-transparent border-0 p-0 text-md transition-2 w-100 text-start py-8 px-12 rounded-8 ${!selectedCategory ? 'bg-main-50 text-main-600 fw-bold' : ''}`}
                    >
                      All Products
                    </button>
                  </li>
                  {categories.map((cat) => (
                    <li key={cat.id} className='mb-12'>
                      <button
                        onClick={() => handleCategoryClick(cat)}
                        className={`text-gray-900 hover-text-main-600 bg-transparent border-0 p-0 text-md transition-2 w-100 text-start py-8 px-12 rounded-8 ${selectedCategory?.id === cat.id ? 'bg-main-50 text-main-600 fw-bold' : ''}`}
                      >
                        {cat.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className='shop-sidebar__box border border-gray-100 rounded-16 p-32 mb-32 bg-white shadow-sm'>
                <h6 className='text-xl border-bottom border-gray-100 pb-24 mb-24 fw-bold'>
                  Sort By
                </h6>
                <select
                  className='form-select rounded-12 border-gray-100 h-44 ps-16 pe-40 py-10 text-gray-600'
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="default">Default Sorting</option>
                  <option value="newest">Newest Arrivals</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="name-az">Name: A to Z</option>
                </select>
              </div>

              {/* <div className='shop-sidebar__box border border-gray-100 rounded-16 p-32 mb-32 bg-white shadow-sm'>
                <h6 className='text-xl border-bottom border-gray-100 pb-24 mb-24 fw-bold'>
                  Filter by Price
                </h6>
                <div className='custom--range px-10'>
                  <ReactSlider
                    className='horizontal-slider'
                    thumbClassName='example-thumb'
                    trackClassName='example-track'
                    value={priceRange}
                    onChange={handlePriceChange}
                    max={10000}
                    min={0}
                    ariaLabel={["Lower thumb", "Upper thumb"]}
                    renderThumb={(props, state) => {
                        const { key, ...restProps } = props;
                        return (
                            <div {...restProps} key={key} className="example-thumb">
                                <div className="thumb-label"></div>
                            </div>
                        );
                    }}
                    pearling
                    minDistance={100}
                  />
                  <div className='mt-32'>
                    <div className="price-display text-gray-700 fw-bold text-sm mb-16">
                        Price: <span className="text-main-600">Rs.{priceRange[0]} - Rs.{priceRange[1]}</span>
                    </div>
                    <button
                      type='button'
                      onClick={handleFilterClick}
                      className='btn bg-main-600 text-white w-100 py-12 rounded-12 fw-bold hover-bg-main-700 transition-2 shadow-sm'
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div> */}
            </div>
          </div>
          {/* Sidebar End */}

          {/* Content Start */}
          <div className='col-lg-9'>
            {/* Top Start */}
            <div className='flex-between gap-16 flex-wrap mb-40 bg-white p-16 rounded-16 border border-gray-100 shadow-sm'>
              <div className='search-form position-relative flex-grow-1 me-32'>
                <input
                  type='text'
                  className='form-control rounded-12 ps-44 pe-44 py-10 h-48 border-gray-100 shadow-none focus-border-main-600'
                  placeholder='Search products...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className='position-absolute top-50 translate-middle-y ms-16 text-gray-400'>
                  <i className='ph ph-magnifying-glass' />
                </span>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    type='button'
                    className='position-absolute top-50 translate-middle-y end-0 me-16 text-gray-400 hover-text-main-600 transition-1'
                    title="Clear search"
                  >
                    <i className='ph-bold ph-x' />
                  </button>
                )}
              </div>

              <div className='flex-align gap-16 flex-wrap'>
                <span className='text-gray-900 fw-bold ms-8'>
                  Showing {filteredProducts.length} Results
                </span>

                <div className='list-grid-btns d-flex gap-12 ms-8'>
                  <button
                    onClick={() => setGrid(false)}
                    type='button'
                    className={`w-44 h-44 flex-center border rounded-12 text-2xl transition-2 ${!grid ? "border-main-600 text-white bg-main-600 shadow-sm" : "border-gray-100 text-gray-400 hover-border-main-600 hover-text-main-600"
                      }`}
                    title="Grid View (4 columns)"
                  >
                    <i className='ph-bold ph-squares-four' />
                  </button>
                  <button
                    onClick={() => setGrid(true)}
                    type='button'
                    className={`w-44 h-44 flex-center border rounded-12 text-2xl transition-2 ${grid ? "border-main-600 text-white bg-main-600 shadow-sm" : "border-gray-100 text-gray-400 hover-border-main-600 hover-text-main-600"
                      }`}
                    title="Table View (2 columns)"
                  >
                    <i className='ph-bold ph-list-dashes' />
                  </button>
                </div>
              </div>
            </div>
            {/* Top End */}

            <div className={`row gy-4 ${grid ? "list-view" : ""}`}>
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product) => (
                  <div key={product.id} className={grid ? "col-6" : "col-xl-3 col-sm-6"}>
                    <ProductCard product={product} />
                  </div>
                ))
              ) : (
                <div className="col-12 text-center py-80">
                  <i className="ph ph-magnifying-glass text-6xl text-gray-200 mb-16 d-block" />
                  <h5 className="text-gray-400">No products match your filters.</h5>
                  <button
                    onClick={() => {
                      setPriceRange([0, 10000]);
                      setSelectedCategory(null);
                      setSearchTerm("");
                      setSortBy("default");
                    }}
                    className="btn text-main-600 fw-bold mt-12 hover-text-decoration-underline"
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          </div>
          {/* Content End */}
        </div>
      </div>
    </section>
  );
};

export default ShopSection;